import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import aiService from '../services/aiService';
import {
  X,
  Check,
  Star,
  CreditCard,
  Shield,
  Zap,
  Crown,
  Sparkles,
  Download,
  FileText,
  Users,
  MessageSquare,
  Target
} from 'lucide-react';

const PaymentModal = ({ isOpen, onClose, onSuccess, userId }) => {
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [plans, setPlans] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      const response = await aiService.getPlans();
      setPlans(response.plans);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const handlePayment = async () => {
    if (!selectedPlan || selectedPlan === 'free') return;

    setIsProcessing(true);
    setError('');

    try {
      // Create payment order
      const orderResponse = await aiService.createPaymentOrder(selectedPlan, userId || '');

      // Initialize Razorpay
      const options = {
        key: orderResponse.razorpay_key_id,
        amount: orderResponse.amount * 100, // Convert to paise
        currency: orderResponse.currency,
        name: 'Shortlistly',
        description: `${orderResponse.plan_name} - Resume Optimization`,
        order_id: orderResponse.order_id,
        handler: async (response) => {
          try {
            console.log('💳 Razorpay payment completed, verifying...', response.razorpay_payment_id);
            
            // Verify payment
            const verifyResponse = await aiService.verifyPayment(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature,
              userId
            );

            console.log('✅ Payment verification response:', verifyResponse);

            if (verifyResponse.status === 'paid') {
              console.log('🎉 Payment verified successfully, calling onSuccess...');
              // Payment successful - call onSuccess after a delay to ensure backend processing
              setTimeout(() => {
                onSuccess();
              }, 500);
            } else {
              console.error('❌ Payment verification failed:', verifyResponse);
              setError('Payment verification failed');
            }
          } catch (error) {
            console.error('❌ Payment verification failed:', error);
            setError('Payment verification failed: ' + error.message);
          }
          setIsProcessing(false);
        },
        prefill: {
          name: 'Resume User',
          email: 'user@example.com'
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
          }
        }
      };

      // Check if Razorpay is loaded
      if (typeof window.Razorpay !== 'undefined') {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        // Load Razorpay script dynamically
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        };
        script.onerror = () => {
          setError('Failed to load payment gateway. Please try again.');
          setIsProcessing(false);
        };
        document.body.appendChild(script);
      }

    } catch (error) {
      console.error('Payment initialization failed:', error);
      setError('Payment initialization failed: ' + error.message);
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const planDetails = [
    {
      id: 'basic',
      highlight: 'Most Popular',
      icon: Zap,
      color: 'blue',
      popular: true
    },
    {
      id: 'premium',
      highlight: 'Best Value',
      icon: Crown,
      color: 'purple',
      popular: false
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">Unlock AI Resume Optimization</h2>
                <p className="text-slate-600 mt-2">Transform your career with professional resume optimization</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}

          {/* Pricing Plans */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {planDetails.map((plan) => {
                const planData = plans[plan.id];
                if (!planData) return null;

                const isSelected = selectedPlan === plan.id;
                const Icon = plan.icon;

                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                      isSelected
                        ? `border-${plan.color}-500 bg-${plan.color}-50 shadow-lg`
                        : 'border-slate-200 hover:border-slate-300'
                    } ${plan.popular ? 'ring-2 ring-blue-200' : ''}`}
                  >
                    {/* Popular Badge */}
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                          {plan.highlight}
                        </div>
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-r from-${plan.color}-500 to-${plan.color}-600 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{planData.name}</h3>
                      <div className="mt-2">
                        <span className="text-4xl font-bold text-slate-900">₹{planData.price}</span>
                        <span className="text-slate-600 ml-1">one-time</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {planData.features.slice(0, 6).map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <Check className={`w-5 h-5 text-${plan.color}-500 mr-3 mt-0.5 flex-shrink-0`} />
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </div>
                      ))}
                      {planData.features.length > 6 && (
                        <div className="text-sm text-slate-500 ml-8">
                          +{planData.features.length - 6} more features
                        </div>
                      )}
                    </div>

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className={`absolute top-4 right-4 w-6 h-6 bg-${plan.color}-500 rounded-full flex items-center justify-center`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Benefits Section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">What You Get Immediately</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-slate-900">95% ATS Score</h4>
                  <p className="text-sm text-slate-600">Optimized for all applicant tracking systems</p>
                </div>
                <div className="text-center">
                  <FileText className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-slate-900">Professional Templates</h4>
                  <p className="text-sm text-slate-600">Multiple formats ready for download</p>
                </div>
                <div className="text-center">
                  <Download className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-slate-900">Instant Download</h4>
                  <p className="text-sm text-slate-600">PDF and DOCX formats included</p>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center justify-center space-x-8 text-sm text-slate-600">
                <div className="flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  Secure Payment
                </div>
                <div className="flex items-center">
                  <Star className="w-4 h-4 mr-2" />
                  4.9/5 Rating
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  50K+ Users
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="mt-8">
              <button
                onClick={handlePayment}
                disabled={isProcessing || !selectedPlan || selectedPlan === 'free'}
                className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-2xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ₹{plans[selectedPlan]?.price} - Get Optimized Resume
                  </>
                )}
              </button>
              
              <p className="text-center text-sm text-slate-500 mt-3">
                Secure payment powered by Razorpay • 7-day money-back guarantee
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentModal;